// Self-contained speciality options for the search feature.

export const SPEC_OPTIONS = [
  { slug: 'aesthetics-clinic', title: 'Aesthetics' },
  { slug: 'audiology-hearing-test-battersea-london', title: 'Audiology' },
  { slug: 'blood-test', title: 'Blood Tests' },
  { slug: 'bone-scan', title: 'Bone Scan' },
  { slug: 'breast-clinic', title: 'Breast Clinic' },
  { slug: 'cardiology', title: 'Cardiology' },
  {
    slug: 'childrens-health-paediatrics',
    title: "Children's Health",
  },
  { slug: 'dental-services', title: 'Dental Services' },
  { slug: 'dermatology', title: 'Dermatology' },
  { slug: 'dietetics', title: 'Dietetics' },
  { slug: 'ent', title: 'ENT' },
  { slug: 'exroid', title: 'Exroid (Haemorrhoids)' },
  { slug: 'female-fertility', title: 'Fertility' },
  { slug: 'gastric-alimetry', title: 'Gastric Alimetry' },
  { slug: 'general-surgery', title: 'General Surgery' },
  { slug: 'geriatrics', title: 'Geriatrics' },
  { slug: 'gp-membership', title: 'GP Membership' },
  { slug: 'private-gp', title: 'GP (General Practice)' },
  { slug: 'gynaecology-and-obstetrics', title: 'Gynaecology & Obstetrics' },
  { slug: 'gut-health-test', title: 'Gut Health Test' },
  { slug: 'haematology', title: 'Haematology' },
  { slug: 'hearing-aids-london', title: 'Hearing Aids' },
  { slug: 'iron-infusion-london', title: 'Iron Infusion' },
  { slug: 'hip-replacement-and-knee-replacement', title: 'Hip & Knee Surgery' },
  { slug: 'arthrosamid', title: 'Arthrosamid Joint Injection' },
  { slug: 'liver-health-liver-scans', title: 'Liver Health (Fibroscan)' },
  { slug: 'maxillofacial', title: 'Maxillofacial Surgery' },
  { slug: 'menopause-clinic', title: 'Menopause' },
  { slug: 'mole-mapping', title: 'Mole Mapping' },
  { slug: 'neurology', title: 'Neurology' },
  { slug: 'neurosurgery', title: 'Spine & Neurosurgery' },
  { slug: 'ophthalmology', title: 'Ophthalmology' },
  { slug: 'obstructive-sleep-apnoea', title: 'Obstructive Sleep Apnoea' },
  { slug: 'physiotherapy', title: 'Physiotherapy' },
  { slug: 'plastic-surgery', title: 'Plastic Surgery' },
  { slug: 'psychiatry', title: 'Psychiatry' },
  { slug: 'radiology', title: 'Imaging' },
  { slug: 'rheumatology', title: 'Rheumatology' },
  { slug: 'sexual-health', title: 'Sexual Health' },
  { slug: 'lumps-and-bumps', title: 'Lumps & Bumps' },
  {
    slug: 'trauma-and-orthopaedics-lower-limb',
    title: 'Trauma & Orthopaedics (Lower Limb)',
  },
  {
    slug: 'trauma-and-orthopaedics-upper-limb',
    title: 'Trauma & Orthopaedics (Upper Limb)',
  },
  { slug: 'trt', title: 'TRT' },
  { slug: 'ultrasound', title: 'Ultrasound' },
  { slug: 'emergency-clinic-london', title: 'Urgent Care' },
  { slug: 'urology-and-andrology', title: 'Urology & Andrology' },
  { slug: 'tmj-specialist-london', title: 'Jaw Pain & TMJ' },
  { slug: 'emg-nerve-conduction-test', title: 'EMG & Nerve Conduction Test' },
] as const;

export const SPEC_OPTION_TITLE_BY_SLUG: Record<string, string> = {
  ...Object.fromEntries(
    SPEC_OPTIONS.map((option) => [option.slug, option.title]),
  ),
  fertility: 'Fertility',
};

/** Map speciality display names (as carried by generated/doctors.json) to
 * the SPEC_OPTION slug used by the search/listing UI.
 */
const SPECIALITY_NAME_TO_SPEC_SLUG: Record<string, string> = {
  // Ophthalmology
  'cataract surgery': 'ophthalmology',
  cataracts: 'ophthalmology',
  'diabetic retinopathy': 'ophthalmology',
  'macular degeneration': 'ophthalmology',
  'ophthalmic anaesthesia': 'ophthalmology',
  'ophthalmic anesthesia': 'ophthalmology',
  'oculoplastic surgery': 'ophthalmology',
  'eyelid surgery': 'ophthalmology',
  'general ophthalmology': 'ophthalmology',
  'neuro-ophthalmology': 'ophthalmology',
  ophthalmology: 'ophthalmology',

  // Haematology
  'blood cancer': 'haematology',
  'blood disorders': 'haematology',
  'blood tests': 'blood-test',
  'haemato-oncology': 'haematology',
  haematology: 'haematology',
  'stem cell transplantation': 'haematology',
  lymphoma: 'haematology',

  // Neurology/Neurosurgery
  neurology: 'neurology',
  neurosurgery: 'neurosurgery',
  'oncoplastic breast surgery': 'oncoplastic-breast-surgery',
  'oral surgery': 'maxillofacial',
  'spinal surgery': 'neurosurgery',
  'brain injury': 'neurosurgery',
  stroke: 'neurosurgery',
  'movement disorders': 'neurology',
  'parkinson disease': 'neurology',
  epilepsy: 'neurology',
  'neuro-diagnostics': 'neurology',
  'neuro-oncology': 'neurology',

  // Cardiology
  cardiology: 'cardiology',
  'clinical cardiology': 'cardiology',
  'cardiac electrophysiology': 'cardiology',
  'cardio-oncology': 'cardiology',
  'preventive cardiology': 'cardiology',
  'heart failure': 'cardiology',
  'ischaemic heart disease': 'cardiology',
  'coronary & ischaemic heart disease': 'cardiology',

  // Surgery
  'general surgery': 'general-surgery',
  'cosmetic surgery': 'aesthetics-clinic',
  'aesthetic/cosmetic surgery': 'aesthetics-clinic',
  aesthetics: 'aesthetics-clinic',
  'plastic surgery': 'plastic-surgery',
  'reconstruction surgery': 'plastic-surgery',
  'breast cancer surgery': 'breast-clinic',
  'breast reconstruction': 'breast-clinic',
  'bariatric surgery': 'general-surgery',
  'laparoscopy surgery': 'general-surgery',

  // Orthopaedics
  'trauma and orthopaedics': 'trauma-and-orthopaedics',
  'trauma and orthopedics': 'trauma-and-orthopaedics',
  'hip replacement': 'hip-replacement-and-knee-replacement',
  'knee replacement': 'hip-replacement-and-knee-replacement',
  'hip and knee surgery': 'hip-replacement-and-knee-replacement',

  // ENT
  ent: 'ent',
  audiology: 'audiology-hearing-test-battersea-london',
  'audiovestibular medicine': 'audiology-hearing-test-battersea-london',

  // Rheumatology
  rheumatology: 'rheumatology',

  // Geriatrics
  geriatrics: 'geriatrics',
  'elderly medicine': 'geriatrics',
  'stroke medicine': 'neurosurgery',

  // General Practice
  'general practitioner': 'private-gp',
  'general practice': 'private-gp',
  gp: 'private-gp',

  // Gastroenterology-related (only services that exist)
  haemorrhoids: 'exroid',
  'obesity & metabolic': 'general-surgery',

  // Anaesthesia sub-specialties that map to their related clinical service
  'ophtalmic anaesthesia': 'ophthalmology',

  // Other
  'breast cancer': 'breast-clinic',
  'breast cancer risk assessment': 'breast-clinic',
  'breast health': 'breast-clinic',
  'breast oncology': 'breast-clinic',
  'breast radiology': 'radiology',
  'sexual function': 'sexual-health',
  'sexual health': 'sexual-health',
  "women's health": 'gynaecology-and-obstetrics',
  menopause: 'menopause-clinic',
  gynaecology: 'gynaecology-and-obstetrics',
  'gynaecology & obstetrics': 'gynaecology-and-obstetrics',
  obstetrics: 'gynaecology-and-obstetrics',
  fertility: 'female-fertility',
  'reproductive medicine': 'female-fertility',

  // Radiology/Diagnostic
  imaging: 'radiology',
  radiology: 'radiology',
  'radiology (imaging)': 'radiology',
  'paediatric radiology': 'radiology',

  // Dermatology
  dermatology: 'dermatology',
  'skin oncology': 'dermatology',
  'skin cancer surgery': 'dermatology',
  psychodermatology: 'dermatology',

  // Psychiatry
  psychiatry: 'psychiatry',
  'adult psychiatry': 'psychiatry',
  'addiction psychiatry': 'psychiatry',

  // Dietetics/Nutrition
  dietetics: 'dietetics',
  dietitian: 'dietetics',
  'nutrition & natural health': 'dietetics',
  nutritionist: 'dietetics',

  // Physiotherapy
  physiotherapy: 'physiotherapy',
  'musculoskeletal physiotherapy': 'physiotherapy',
  'manual therapy': 'physiotherapy',
  'post-operative rehabilitation': 'physiotherapy',
  'orthopaedic rehabilitation': 'physiotherapy',

  // Urology
  urology: 'urology-and-andrology',
  'urology & andrology': 'urology-and-andrology',
  endourology: 'urology-and-andrology',
  andrology: 'urology-and-andrology',

  // Emergency
  'emergency medicine': 'emergency-clinic-london',
};

/**
 * Resolve a speciality display name (as found in generated/doctors.json) to the
 * closest SPEC_OPTION slug. Returns the slug or null when no mapping exists.
 */
export function specialityNameToSpecSlug(
  name: string | undefined | null,
): string | null {
  if (!name) return null;
  const key = name
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '')
    .trim();
  const normalized = Object.fromEntries(
    Object.entries(SPECIALITY_NAME_TO_SPEC_SLUG).map(([k, v]) => [
      k
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '')
        .trim(),
      v,
    ]),
  );
  return normalized[key] ?? null;
}
